import { hubPageBg } from './accountHubStyles';
import { downloadCodes } from './accountHubUtils';
import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useDispatch } from 'react-redux';
import { useAsUserParam } from './useAsUserParam';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PageHeader from 'src/components/NewRenterInterface/PageHeader/PageHeader';
import ProxyBanner from './ProxyBanner';
import { RENTER_PATHS } from 'src/routes/renterPaths';
import OtpCodeInput from 'src/components/shared/OtpCodeInput';
import axios from 'src/utils/axios';
import { setElevatedAuthTotpEnabled } from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceUserSlice';

type SetupStep = 'loading' | 'qr' | 'verify' | 'done' | 'error';

interface EnrollResponse {
  secret_b32: string;
  otpauth_uri: string;
  qr_png_b64: string;
}

const TFASetupPage: FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const asUser = useAsUserParam();

  const backRoute = `/${RENTER_PATHS.HOME}/${RENTER_PATHS.ACCOUNT_HUB}/${userId}/${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY}/${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY_TFA}`;
  const finishRoute = `/${RENTER_PATHS.HOME}/${RENTER_PATHS.ACCOUNT_HUB}/${userId}/${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY}`;

  const [step, setStep] = useState<SetupStep>('loading');
  const [enrollData, setEnrollData] = useState<EnrollResponse | null>(null);
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [acknowledged, setAcknowledged] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const startEnrollment = async (): Promise<void> => {
      // Check current TOTP status first — avoids a 409 if the device is
      // already confirmed (e.g. HMR re-mount after completing setup).
      try {
        const statusResp = await axios.get(`v2/user/totp/enroll/${asUser}`);
        if (statusResp.data?.confirmed) {
          navigate(backRoute, { replace: true });
          return;
        }
      } catch {
        // If status check fails, proceed to start enrollment anyway
      }

      try {
        const resp = await axios.post<EnrollResponse>(
          `v2/user/totp/enroll/${asUser}`
        );
        setEnrollData(resp.data);
        setStep('qr');
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response
          ?.status;
        if (status === 409) {
          navigate(backRoute, { replace: true });
        } else {
          setStep('error');
        }
      }
    };

    void startEnrollment();
  }, [backRoute, navigate, asUser]);

  const handleCopySecret = async (): Promise<void> => {
    if (!enrollData?.secret_b32) {
      return;
    }
    try {
      await navigator.clipboard.writeText(enrollData.secret_b32);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API can be unavailable (insecure context, denied permission).
      // Leave the secret visible on-screen for manual copy; no-op here.
    }
  };

  const handleVerify = async (): Promise<void> => {
    if (code.length < 6) {
      return;
    }
    setVerifying(true);
    setVerifyError(null);
    try {
      const resp = await axios.post(`v2/user/totp/enroll/${asUser}`, { code });
      setBackupCodes(
        (resp.data as { recovery_codes?: string[] }).recovery_codes ?? []
      );
      setStep('done');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error ?? 'Invalid code. Please try again.';
      setVerifyError(msg);
    } finally {
      setVerifying(false);
    }
  };

  const handleFinish = (): void => {
    // Only update totp_enabled — do not touch the verified/expires state.
    // Completing setup is not the same as verifying via TOTP.
    dispatch(setElevatedAuthTotpEnabled(true));
    navigate(finishRoute, { replace: true });
  };

  return (
    <Box sx={{ backgroundColor: hubPageBg, minHeight: '100%' }}>
      <PageHeader
        title="2FA Setup"
        icon={<SecurityIcon />}
        backRoute={backRoute}
        subtitle="Scan the QR code with your authenticator app"
      />
      <ProxyBanner />

      {step === 'loading' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {step === 'error' && (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            Could not start 2FA setup. Please try again later.
          </Alert>
        </Box>
      )}

      {step === 'qr' && enrollData && (
        <Stack gap={2.5} sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Step 1 of 2
          </Typography>
          <LinearProgress variant="determinate" value={50} />

          <Box
            sx={{
              backgroundColor: 'background.paper',
              borderRadius: 2,
              boxShadow: '0px 2px 10px rgba(0,0,0,0.063)',
              p: 2,
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              1. Open your authenticator app
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Google Authenticator, Authy, or similar
            </Typography>

            {/* QR code */}
            <Box
              sx={{
                my: 2,
                display: 'flex',
                justifyContent: 'center',
                backgroundColor: 'common.white',
                p: 2,
                borderRadius: 1,
              }}
            >
              <img
                src={`data:image/png;base64,${enrollData.qr_png_b64}`}
                alt="QR Code for authenticator setup"
                style={{ width: 240, height: 240, imageRendering: 'pixelated' }}
              />
            </Box>

            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              2. Scan this QR code
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 2 }}
            >
              Point your authenticator app at the code above. Once it shows a
              6-digit number, tap the button below.
            </Typography>

            {/* Next button lives here — right after the instruction so it's
                impossible to miss after scanning */}
            <Button
              variant="contained"
              fullWidth
              onClick={() => setStep('verify')}
              sx={{
                borderRadius: '12px',
                fontWeight: 600,
                py: 1.5,
                fontSize: 15,
              }}
            >
              Next
            </Button>
          </Box>

          {/* Manual entry — secondary option, collapsed visually */}
          <Box
            sx={{
              backgroundColor: 'background.paper',
              borderRadius: 2,
              boxShadow: '0px 2px 10px rgba(0,0,0,0.063)',
              p: 2,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
              >
                Can&apos;t scan? Enter manually
              </Typography>
              <Button
                size="small"
                startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                onClick={() => void handleCopySecret()}
                sx={{ fontSize: 12, ml: 1, flexShrink: 0 }}
              >
                <span>{copied ? 'Copied!' : 'Copy'}</span>
              </Button>
            </Stack>
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'monospace',
                letterSpacing: 1,
                wordBreak: 'break-all',
                color: 'text.primary',
                mt: 0.5,
              }}
            >
              {enrollData.secret_b32}
            </Typography>
          </Box>
        </Stack>
      )}

      {step === 'verify' && (
        <Stack gap={2.5} sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Step 2 of 2
          </Typography>
          <LinearProgress variant="determinate" value={100} />

          <Box
            sx={{
              backgroundColor: 'background.paper',
              borderRadius: 2,
              boxShadow: '0px 2px 10px rgba(0,0,0,0.063)',
              p: 2,
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              Verify your authenticator
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 2 }}
            >
              Enter the 6-digit code from your authenticator app to confirm
              setup.
            </Typography>

            {verifyError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {verifyError}
              </Alert>
            )}

            <OtpCodeInput
              value={code}
              onChange={setCode}
              disabled={verifying}
              error={Boolean(verifyError)}
            />
          </Box>

          <Button
            variant="contained"
            fullWidth
            disabled={code.length < 6 || verifying}
            onClick={() => void handleVerify()}
            startIcon={verifying ? <CircularProgress size={16} /> : null}
            sx={{ borderRadius: 1.5 }}
          >
            <span>{verifying ? 'Verifying…' : 'Confirm Setup'}</span>
          </Button>
        </Stack>
      )}

      {step === 'done' && (
        <Stack gap={2.5} sx={{ p: 3 }}>
          <Alert severity="success">
            Two-Factor Authentication has been enabled!
          </Alert>

          <Box
            sx={{
              backgroundColor: 'background.paper',
              borderRadius: 2,
              boxShadow: '0px 2px 10px rgba(0,0,0,0.063)',
              p: 2,
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              Save your backup codes
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 2 }}
            >
              These codes can be used to access your account if you lose access
              to your authenticator. Each code can only be used once. Store them
              somewhere safe.
            </Typography>
            <Box
              sx={{
                backgroundColor: 'background.default',
                borderRadius: 1,
                p: 1.5,
                fontFamily: 'monospace',
                fontSize: 14,
                mb: 2,
              }}
            >
              {backupCodes.map((c) => (
                <Typography
                  key={c}
                  variant="body2"
                  sx={{ fontFamily: 'monospace', lineHeight: 1.8 }}
                >
                  {c}
                </Typography>
              ))}
            </Box>

            <Alert severity="warning" icon={false} sx={{ mb: 2 }}>
              <Typography variant="caption">
                These codes are shown <strong>only once</strong>. Download or
                write them down before continuing.
              </Typography>
            </Alert>

            <Button
              variant="outlined"
              fullWidth
              size="small"
              onClick={() => downloadCodes(backupCodes)}
              sx={{ mb: 2, borderRadius: '8px' }}
            >
              Download
            </Button>

            <Stack direction="row" alignItems="center" gap={1}>
              <input
                type="checkbox"
                id="ack-codes"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
              />
              <label htmlFor="ack-codes">
                <Typography variant="body2">
                  I have saved my backup codes in a safe place
                </Typography>
              </label>
            </Stack>
          </Box>

          <Button
            variant="contained"
            fullWidth
            disabled={!acknowledged}
            onClick={handleFinish}
            sx={{ borderRadius: 1.5 }}
          >
            Finish Setup
          </Button>
        </Stack>
      )}
    </Box>
  );
};

export default TFASetupPage;
